/**
 * TopCitiesGrid — shared "Top destinations" rail used by /stay, /table
 * and /enjoy. Mirrors the visual rail on /escape (EscapeTopDestinations)
 * so a member feels at home moving between pillars.
 *
 * Founder ruling 28 Feb 2026 (PM):
 *   "I don't see the main cities we decided to put on stay table enjoy
 *    like we have in escape on the front page."
 *
 * Doctrine:
 *   • Per-pillar city tunings — `/stay` favours beach/leisure
 *     destinations; `/enjoy` favours nightlife capitals; `/table`
 *     favours dining capitals.
 *   • Every tile click pre-fills the search and runs it on the host
 *     pillar — no detour to a separate landing page (kept simple
 *     intentionally; we can graduate to dedicated /destination/:slug
 *     routes later, matching /escape's pattern).
 *   • Same Unsplash photo IDs as /escape where the city overlaps —
 *     proven URLs, zero new infra, free for commercial use.
 *   • Fallback to a Concierge® brass monogram if the photo CDN ever
 *     404s · no card ever appears empty.
 */
import React, { useState } from "react";

const T = {
  ink: "#211C15",
  ink3: "#8A8071",
  line: "#E5DCCB",
  brass: "#8B6914",
};

// Master city catalogue · stable Unsplash photo IDs (verified Feb 2026).
const CITY = {
  paris:      { name: "Paris",      query: "Paris",      sub: "France · Lights & Louvre",
                image: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800&q=80&auto=format&fit=crop" },
  dubai:      { name: "Dubai",      query: "Dubai",      sub: "United Arab Emirates",
                image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=80&auto=format&fit=crop" },
  london:     { name: "London",     query: "London",     sub: "United Kingdom",
                image: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=800&q=80&auto=format&fit=crop" },
  rome:       { name: "Rome",       query: "Rome",       sub: "Italy · The Eternal City",
                image: "https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=800&q=80&auto=format&fit=crop" },
  newyork:    { name: "New York",   query: "New York",   sub: "United States",
                image: "https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=800&q=80&auto=format&fit=crop" },
  tokyo:      { name: "Tokyo",      query: "Tokyo",      sub: "Japan · Old and new",
                image: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=800&q=80&auto=format&fit=crop" },
  singapore:  { name: "Singapore",  query: "Singapore",  sub: "Garden city",
                image: "https://images.unsplash.com/photo-1565967511849-76a60a516170?w=800&q=80&auto=format&fit=crop" },
  barcelona:  { name: "Barcelona",  query: "Barcelona",  sub: "Spain · Catalonia",
                image: "https://images.unsplash.com/photo-1583422409516-2895a77efded?w=800&q=80&auto=format&fit=crop" },
  vegas:      { name: "Las Vegas",  query: "Las Vegas",  sub: "United States · Nevada",
                image: "https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?w=800&q=80&auto=format&fit=crop" },
  sttropez:   { name: "St-Tropez",  query: "St Tropez",  sub: "France · French Riviera",
                image: "https://images.unsplash.com/photo-1499678329028-101435549a4e?w=800&q=80&auto=format&fit=crop" },
  // /stay-leaning beach + resort cities
  bali:       { name: "Bali",       query: "Bali",       sub: "Indonesia · Island & retreat",
                image: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=800&q=80&auto=format&fit=crop" },
  maldives:   { name: "Maldives",   query: "Maldives",   sub: "Indian Ocean · Atoll",
                image: "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=800&q=80&auto=format&fit=crop" },
  goa:        { name: "Goa",        query: "Goa",        sub: "India · Sand & shacks",
                image: "https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=800&q=80&auto=format&fit=crop" },
  // /enjoy-leaning entertainment cities
  ibiza:      { name: "Ibiza",      query: "Ibiza",      sub: "Spain · Balearic nights",
                image: "https://images.unsplash.com/photo-1473496169904-658ba7c44d8a?w=800&q=80&auto=format&fit=crop" },
  berlin:     { name: "Berlin",     query: "Berlin",     sub: "Germany · After dark",
                image: "https://images.unsplash.com/photo-1587330979470-3016b6702d89?w=800&q=80&auto=format&fit=crop" },
  // /table-leaning dining capitals
  mumbai:     { name: "Mumbai",     query: "Mumbai",     sub: "India · The Table city",
                image: "https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=800&q=80&auto=format&fit=crop" },
  bangkok:    { name: "Bangkok",    query: "Bangkok",    sub: "Thailand · Street to fine",
                image: "https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=800&q=80&auto=format&fit=crop" },
  // /fit-leaning wellness capitals
  ubud:       { name: "Ubud",       query: "Ubud",       sub: "Bali · Jungle & yoga",
                image: "https://images.unsplash.com/photo-1604999333679-b86d54738315?w=800&q=80&auto=format&fit=crop" },
  rishikesh:  { name: "Rishikesh",  query: "Rishikesh",  sub: "India · Yoga capital",
                image: "https://images.unsplash.com/photo-1599056407101-7c557a4a0144?w=800&q=80&auto=format&fit=crop" },
  tulum:      { name: "Tulum",      query: "Tulum",      sub: "Mexico · Cenotes & cacao",
                image: "https://images.unsplash.com/photo-1518638150340-f706e86654de?w=800&q=80&auto=format&fit=crop" },
  phuket:     { name: "Phuket",     query: "Phuket",     sub: "Thailand · Beach wellness",
                image: "https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?w=800&q=80&auto=format&fit=crop" },
  mykonos:    { name: "Mykonos",    query: "Mykonos",    sub: "Greece · Aegean retreat",
                image: "https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=800&q=80&auto=format&fit=crop" },
  // /exclusive-leaning Maison capitals
  milan:      { name: "Milan",      query: "Milan",      sub: "Italy · Fashion & design",
                image: "https://images.unsplash.com/photo-1520440229-6469a149ac59?w=800&q=80&auto=format&fit=crop" },
  hongkong:   { name: "Hong Kong",  query: "Hong Kong",  sub: "Hong Kong SAR",
                image: "https://images.unsplash.com/photo-1536599018102-9f803c140fc1?w=800&q=80&auto=format&fit=crop" },
  losangeles: { name: "Los Angeles",query: "Los Angeles",sub: "United States · California",
                image: "https://images.unsplash.com/photo-1518533954129-7774297db60a?w=800&q=80&auto=format&fit=crop" },
  seoul:      { name: "Seoul",      query: "Seoul",      sub: "South Korea",
                image: "https://images.unsplash.com/photo-1538669715315-155098f0fb1d?w=800&q=80&auto=format&fit=crop" },
  madrid:     { name: "Madrid",     query: "Madrid",     sub: "Spain · The capital",
                image: "https://images.unsplash.com/photo-1543783207-ec64e4d95325?w=800&q=80&auto=format&fit=crop" },
  florence:   { name: "Florence",   query: "Florence",   sub: "Italy · Tuscan craft",
                image: "https://images.unsplash.com/photo-1543429776-2782fc8e1acd?w=800&q=80&auto=format&fit=crop" },
  geneva:     { name: "Geneva",     query: "Geneva",     sub: "Switzerland · Watchmaking",
                image: "https://images.unsplash.com/photo-1527668752968-14dc70a27c95?w=800&q=80&auto=format&fit=crop" },
  shanghai:   { name: "Shanghai",   query: "Shanghai",   sub: "China · The Bund",
                image: "https://images.unsplash.com/photo-1545893835-abaa50cbe628?w=800&q=80&auto=format&fit=crop" },
};

// Per-pillar curated picks · 10 cities each · order is editorial.
export const PICKS_STAY = [
  CITY.paris, CITY.dubai, CITY.bali, CITY.maldives,
  CITY.london, CITY.rome, CITY.newyork, CITY.tokyo,
  CITY.goa, CITY.singapore,
];

export const PICKS_TABLE = [
  CITY.paris, CITY.tokyo, CITY.london, CITY.newyork,
  CITY.mumbai, CITY.rome, CITY.dubai, CITY.bangkok,
  CITY.barcelona, CITY.singapore,
];

export const PICKS_ENJOY = [
  CITY.london, CITY.newyork, CITY.berlin, CITY.ibiza,
  CITY.paris, CITY.vegas, CITY.dubai, CITY.barcelona,
  CITY.tokyo, CITY.singapore,
];

// /fit · wellness-tuned picks · island retreats + yoga capitals first,
// then the urban wellness scene (London / Paris / NYC / Dubai gyms
// & spas are world-class even though the city's bias is elsewhere).
export const PICKS_FIT = [
  CITY.bali, CITY.ubud, CITY.rishikesh, CITY.tulum,
  CITY.goa, CITY.phuket, CITY.mykonos, CITY.dubai,
  CITY.london, CITY.newyork,
];

// /exclusive · Maison capitals · 15 cities · order is editorial.
// Founder ruling Feb 2026: 15 cities, 6 categories, brief-only V1.
export const PICKS_MAISON = [
  CITY.paris, CITY.milan, CITY.london, CITY.newyork,
  CITY.tokyo, CITY.hongkong, CITY.dubai, CITY.singapore,
  CITY.mumbai, CITY.geneva, CITY.florence, CITY.madrid,
  CITY.seoul, CITY.shanghai, CITY.losangeles,
];

function MonogramFallback({ name }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <div
      style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(135deg, #F4EFE6 0%, #EFE6D3 100%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        borderRadius: 14,
      }}
      aria-hidden="true"
    >
      <span
        className="t-serif"
        style={{
          fontSize: 64, color: T.brass, fontWeight: 500,
          letterSpacing: "-0.04em", opacity: 0.7,
        }}
      >{initial}</span>
    </div>
  );
}

function CityCard({ city, pillar, onPick }) {
  const [errored, setErrored] = useState(false);
  const slug = city.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");

  return (
    <button
      type="button"
      data-testid={`${pillar}-top-city-${slug}`}
      onClick={() => onPick(city.query)}
      style={{
        background: "transparent",
        border: 0, padding: 0,
        cursor: "pointer", textAlign: "left",
        fontFamily: "inherit",
      }}
    >
      <div
        style={{
          width: "100%", aspectRatio: "4/5",
          borderRadius: 14,
          border: `1px solid ${T.line}`,
          position: "relative",
          overflow: "hidden",
          transition: "transform 0.25s ease, box-shadow 0.25s ease",
          background: errored
            ? "transparent"
            : `linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.55)), url(${city.image}) center/cover no-repeat`,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 10px 30px rgba(33,28,21,0.18)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        {errored && <MonogramFallback name={city.name} />}
        <img
          src={city.image}
          alt={`${city.name} · ${city.sub}`}
          onError={() => setErrored(true)}
          style={{ display: "none" }}
          data-testid={`${pillar}-top-city-img-${slug}`}
        />
        <div style={{
          position: "absolute", bottom: 14, left: 14, right: 14,
          color: errored ? T.ink : "#fff",
        }}>
          <div className="t-serif" style={{ fontSize: 22, lineHeight: 1.1, letterSpacing: "-0.01em" }}>
            {city.name}
          </div>
          <div style={{ fontSize: 12, color: errored ? T.ink3 : "rgba(255,255,255,0.85)", marginTop: 4 }}>
            {city.sub}
          </div>
        </div>
      </div>
    </button>
  );
}

/** Shared TopCitiesGrid · pass the pillar slug + the picks + an
 *  onPick(query) callback that runs the host pillar's search. */
export default function TopCitiesGrid({ pillar, picks, onPick, eyebrow, title }) {
  return (
    <section
      data-testid={`${pillar}-top-cities`}
      style={{ padding: "8px 0 56px" }}
    >
      <div style={{ maxWidth: 1240, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14 }}>
          <div>
            <div style={{
              fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase",
              color: T.brass, fontFamily: "ui-monospace, monospace", marginBottom: 6,
            }}>
              {eyebrow || "Top destinations"}
            </div>
            <div className="t-serif" style={{ fontSize: 22, color: T.ink }}>
              {title || "Cities we know by heart"}
            </div>
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 16,
          }}
        >
          {picks.map((c) => (
            <CityCard key={c.name} city={c} pillar={pillar} onPick={onPick} />
          ))}
        </div>
      </div>
    </section>
  );
}
